import Input from "../../components/Input/Input"
import React, { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import Button from "../../components/Button/Button"
import Calendar from "../../components/Calendar/Calendar"
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { useNavigate, useParams } from "react-router-dom"
import { showSnackBar } from "../Snackbar/Snackbar"
import { getPaymentById, updateTransactionData } from "../../features/payment/paymentSlice"
import { clearNumber } from "../../utils/payment"
import { dateToServer, serverToLocal } from "../../utils/date"


const EditPaymentTransactionData = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { triggerLoading } = useMyContextLoading()
  const { transactionId, contactId } = useParams()

  const [formData, setFormData] = useState({
    reasonText: '',
    reasonCode: '',
    description: '',
    processedAt: '',
    gateway: '',
    comment: '',
  })

  // The form reads processedAt as a full instant (serverToLocal) but the
  // Calendar edits it as a date-only value. To avoid truncating the real
  // processed timestamp when the operator saves without touching the date, we
  // keep the original raw API instant and only rewrite processedAt when the
  // operator actually changed it.
  const originalProcessedAtRef = useRef(null)
  const processedAtDirtyRef = useRef(false)

  useEffect(() => {
    const fetchPayment = async () => {
      triggerLoading(true)
      const response = await dispatch(getPaymentById(transactionId))
      const data = response?.payload?.data?.transactionData || {}
      // Capture the untouched raw instant so an unchanged save is a true no-op
      // for this field (byte-identical round-trip; null/absent stays falsy so
      // the backend leaves the field alone).
      originalProcessedAtRef.current = data.processedAt ?? null
      processedAtDirtyRef.current = false
      setFormData({
        reasonText: data.reasonText || '',
        reasonCode: data.reasonCode || '',
        description: data.description || '',
        processedAt: serverToLocal(data.processedAt) || '',
        gateway: data.gateway || '',
        comment: data.comment || '',
      })
      triggerLoading(false)
    }

    fetchPayment()
  }, [])


  const handleInputChange = ({ name, value }) => {
    if (name === 'processedAt') {
      processedAtDirtyRef.current = true
    }
    if (name === 'reasonCode') {
      const maxLength = 3
      setFormData(prev => ({ ...prev, [name]: clearNumber(value)?.slice(0, maxLength) }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSave = async () => {
    triggerLoading(true)
    const response = await dispatch(updateTransactionData({
      transactionId,
      data: {
        ...formData,
        // Only send a date-only value when the operator actually changed the
        // date; otherwise preserve the original instant exactly.
        processedAt: processedAtDirtyRef.current
          ? dateToServer(formData.processedAt)
          : originalProcessedAtRef.current
      }
    }))
    if (response?.error) {
      showSnackBar("error", "Failed to update transaction data")
    } else {
      showSnackBar("success", "Transaction data successfully updated")
      navigate(`/contact/${contactId}`)
    }
    triggerLoading(false)
  }

  return (
    <ContainerPage>
      <PageTitle title="Edit Transaction Data" />
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px] bg-surface border border-divider rounded-2xl shadow-card p-12">
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 border-b border-divider">
            <Input label="Reason Text"
              placeholder="Reason Text"
              value={formData.reasonText}
              onChange={e => handleInputChange({ name: 'reasonText', value: e.target.value })} />
            <Input label="Reason Code"
              placeholder="Reason Code"
              value={formData.reasonCode}
              onChange={e => handleInputChange({ name: 'reasonCode', value: e.target.value })} />
            <Input label="Description"
              placeholder="Description"
              value={formData.description}
              onChange={e => handleInputChange({ name: 'description', value: e.target.value })} />
            <Calendar label="Processed At"
              placeholder="Processed At"
              value={formData.processedAt}
              onChange={date => handleInputChange({ name: 'processedAt', value: date })} />
            <Input label="Gateway"
              placeholder="Gateway"
              value={formData.gateway}
              onChange={e => handleInputChange({ name: 'gateway', value: e.target.value })} />
            <Input label="Comment"
              placeholder="Comment"
              value={formData.comment}
              onChange={e => handleInputChange({ name: 'comment', value: e.target.value })} />
          </div>
          <div className="mt-6 flex justify-end space-x-4">
            <Button type="outlined" size="lg" onClick={() => navigate(`/contact/${contactId}`)}>Close</Button>
            <Button size="lg" onClick={handleSave} disabled={!formData.processedAt}>Save</Button>
          </div>
        </div>
      </div>
    </ContainerPage>
  )
}


export default EditPaymentTransactionData